import React, {
  useState, useCallback, useMemo, useEffect,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { FileSpreadsheet } from 'lucide-react';

import useLeaveExtraBalanceApi, {
  employeeAsync, toFormValues, toRequest, todayInput, YEAR_OPTIONS, isAutomaticRow,
} from './config';
import {
  getExtraBalanceVariations, createExtraBalance, updateExtraBalance,
} from '../../services/leaveExtraBalanceService';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';

// Ek İzin Bakiyesi — İK'nın bir çalışanın YILLIK ücretli izin bakiyesine kural dışı
// eklediği günler. Liste EmployeesPage deseninde: başlık ve "Yeni" düğmesi DataTable'ın
// içinde, süzgeçler onun araç çubuğunda (UI altın kuralları). Elle giriş
// GenericFormDrawer ile (modal DEĞİL); toplu giriş ayrı içe aktarma sayfasında.
//
// TÜR: yalnız çalışanın politikasındaki yıllık eksenli türler. Tek tür varsa seçici
// GİZLİDİR ve otomatik dolar (sunucu da aynı kuralı uygular); birden çoksa zorunlu.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  /* İç içe scroll yasak — tek kaydırma DataTable satırlarıdır. */
  min-height: 0;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SelectBox = styled.div`
  width: 130px;
`;

const WideSelectBox = styled.div`
  width: 220px;
`;

const ALL = 'all';
const BOS_TURLER = { employeeId: null, options: [], loaded: false };

export default function LeaveExtraBalancePage() {
  const { t } = useTranslation();
  const history = useHistory();

  const [year, setYear] = useState(ALL);
  const [employeeId, setEmployeeId] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageNumber: 1, pageSize: 10 });
  const [drawer, setDrawer] = useState({ open: false, row: null });
  const [turler, setTurler] = useState(BOS_TURLER);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const effectiveParams = useMemo(() => ({
    ...queryParams,
    year: year === ALL ? undefined : year,
    employeeId: employeeId || undefined,
  }), [queryParams, year, employeeId]);

  const {
    fetching, failed, data, pagination, columns: baseColumns, refresh, remove,
  } = useLeaveExtraBalanceApi(effectiveParams);

  // Süzgeç değişince 1. sayfaya dönülür — 3. sayfadayken süzgeç daralınca boş sayfa görünmesin.
  const handleYearChange = useCallback((val) => {
    setYear(val || ALL);
    setQueryParams((prev) => ({ ...prev, pageNumber: 1 }));
  }, []);

  const handleEmployeeFilter = useCallback((val) => {
    setEmployeeId(val || null);
    setQueryParams((prev) => ({ ...prev, pageNumber: 1 }));
  }, []);

  const handleDataStateChange = useCallback((action, tableState) => {
    const parsed = parseTableParams(tableState, baseColumns);
    setQueryParams({ pageNumber: parsed.pageNumber, pageSize: parsed.pageSize });
  }, [baseColumns]);

  // Çalışanın politikasındaki yıllık türler — seçici bunlarla dolar.
  const loadTurler = useCallback(async (empId) => {
    if (!empId) {
      setTurler(BOS_TURLER);
      return null;
    }
    const res = await getExtraBalanceVariations(empId);
    const sonuc = res?.data?.status ? res.data.data : null;
    setTurler({ employeeId: empId, options: sonuc?.options || [], loaded: !!sonuc });
    return sonuc;
  }, []);

  // Düzenlemede kayıtlı çalışanın türleri çekmece açılırken yüklenir; yeni kayıtta sıfırdan.
  useEffect(() => {
    if (!drawer.open) return;
    if (drawer.row) loadTurler(drawer.row.employeeId);
    else setTurler(BOS_TURLER);
  }, [drawer, loadTurler]);

  // Başlangıç değeri SABİT kimlikli olmalı: GenericFormDrawer formu `[open, initialValues]`
  // değişince sıfırlar — her render'da yeni nesne vermek, tür listesi yüklenip sayfa yeniden
  // çizildiğinde kullanıcının girdiği değerleri silerdi.
  const initialValues = useMemo(() => (drawer.row ? toFormValues(drawer.row) : null), [drawer.row]);

  const openCreate = useCallback(() => setDrawer({ open: true, row: null }), []);
  const closeDrawer = useCallback(() => setDrawer((prev) => ({ ...prev, open: false })), []);

  const schema = useMemo(() => ([
    {
      name: 'employeeId',
      label: t('leaveExtraBalance.employee'),
      type: 'asyncSelect',
      required: true,
      onSearch: employeeAsync.onSearch,
      onGetById: employeeAsync.onGetById,
      derives: ['leaveVariationId'],
      // Çalışan değişince türleri çek; tek tür varsa onu seç, yoksa seçimi temizle.
      deriveFields: async (id) => {
        const sonuc = await loadTurler(id);
        return { leaveVariationId: sonuc?.defaultId ?? null };
      },
    },
    {
      name: '_noAnnualVariation',
      type: 'notice',
      tone: 'warning',
      content: t('leaveExtraBalance.noAnnualVariation'),
      hidden: (f) => !f.employeeId || !turler.loaded || turler.options.length > 0,
    },
    {
      name: 'leaveVariationId',
      label: t('leaveExtraBalance.variation'),
      type: 'select',
      required: true,
      options: () => turler.options,
      description: t('leaveExtraBalance.variationHint'),
      hidden: (f) => !f.employeeId || turler.options.length <= 1,
    },
    {
      name: 'days',
      label: t('leaveExtraBalance.days'),
      type: 'number',
      required: true,
      min: 0.5,
      max: 365,
      step: 0.5,
      description: t('leaveExtraBalance.daysHint'),
    },
    {
      name: 'effectiveDate',
      label: t('leaveExtraBalance.effectiveDate'),
      type: 'date',
      required: true,
      defaultValue: todayInput(),
      description: t('leaveExtraBalance.effectiveDateHint'),
    },
    {
      name: 'note',
      label: t('leaveExtraBalance.note'),
      type: 'textarea',
      required: true,
      description: t('leaveExtraBalance.noteHint'),
    },
  ]), [t, turler, loadTurler]);

  const handleSave = useCallback(async (formData) => {
    const payload = toRequest(formData);
    try {
      if (drawer.row) await updateExtraBalance(drawer.row.id, payload);
      else await createExtraBalance(payload);
      refresh();
      return true;
    } catch {
      return false;   // hata toast'ı interceptor'da; çekmece açık kalır
    }
  }, [drawer.row, refresh]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    await remove(deleteTarget.id);
    setDeleting(false);
    setDeleteTarget(null);
  }, [deleteTarget, remove]);

  const buildActions = useCallback((row) => ([
    { type: 'edit', label: t('common.edit'), onClick: () => setDrawer({ open: true, row }) },
    { type: 'delete', label: t('common.delete'), onClick: () => setDeleteTarget(row) },
  ]), [t]);

  const columns = useMemo(() => ([
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false,
        sort: false,
        download: false,
        print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          // Puantajdan doğan otomatik kredi (nöbet / fazla çalışma karşılığı) salt okunur: eylem yok.
          if (!row || isAutomaticRow(row)) return null;
          return <OptionsMenu actions={buildActions(row)} row={row} />;
        },
      },
    },
  ]), [baseColumns, data, buildActions]);

  const yearOptions = useMemo(() => ([
    { id: ALL, name: t('leaveExtraBalance.allYears') },
    ...YEAR_OPTIONS,
  ]), [t]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawer.open}
        onClose={closeDrawer}
        title={drawer.row ? t('leaveExtraBalance.editTitle') : t('leaveExtraBalance.newTitle')}
        schema={schema}
        initialValues={initialValues}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title={t('leaveExtraBalance.deleteTitle')}
        message={t('leaveExtraBalance.deleteMessage', {
          name: deleteTarget?.fullName || '',
          days: deleteTarget?.days ?? '',
        })}
        confirmText={t('common.delete')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        loading={deleting}
      />

      {failed && <LoadErrorBanner message={t('leaveExtraBalance.loadFailed')} />}

      <DataTable
        title={t('leaveExtraBalance.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        disableFilters
        canCreateNewRecord
        newButtonLabel={t('leaveExtraBalance.new')}
        onNewButtonClick={openCreate}
        onTableChanged={handleDataStateChange}
        toolbarActions={(
          <Toolbar>
            <SelectBox>
              <GenericSelect
                label={t('leaveExtraBalance.year')}
                size="small"
                clearable={false}
                options={yearOptions}
                value={year}
                onChange={handleYearChange}
              />
            </SelectBox>
            <WideSelectBox>
              <AsyncGenericSelect
                compact
                label={t('leaveExtraBalance.employee')}
                value={employeeId}
                onChange={handleEmployeeFilter}
                onSearch={employeeAsync.onSearch}
                onGetById={employeeAsync.onGetById}
              />
            </WideSelectBox>
            <Tooltip text={t('leaveExtraBalance.importAction')}>
              <IconButton
                size="sm"
                data-role="extra-balance-import"
                aria-label={t('leaveExtraBalance.importAction')}
                onClick={() => history.push('/leave-extra-balance/import')}
              >
                <FileSpreadsheet size={16} />
              </IconButton>
            </Tooltip>
          </Toolbar>
        )}
      />
    </Container>
  );
}
